import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import { PageHero } from "@/components/base/PageHero";
import Reveal from "@/components/base/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { voices } from "@/mocks/content";
import { siteBrand } from "@/mocks/site";

const aboutSchema = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 暗线独家爆料线索征集中心",
    url: "https://hongshanyang.cn/about",
    description:
      "暗线的品牌介绍、网站定位、发展理念、内容原则、核实小组角色与联系方式，说明我们如何处理每一条独家爆料线索。",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteBrand.name,
    url: "https://hongshanyang.cn/",
    description: "独立核实小组运营的独家爆料线索征集中心，不隶属于任何商业机构。",
    email: siteBrand.email,
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://hongshanyang.cn/" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "https://hongshanyang.cn/about" },
    ],
  },
];

const positioning = [
  {
    icon: "ri-focus-3-line",
    title: "只做一件事：接住线索",
    desc: "我们不是资讯门户，也不做内容聚合。站内所有内容都围绕一个目标：让掌握材料的人，能安全、体面地把它交出来。",
  },
  {
    icon: "ri-scales-3-line",
    title: "以核实为准绳",
    desc: "我们不预设结论，也不为流量选择立场。每一条被呈现的内容，都必须能追溯到具体材料与交叉印证的过程。",
  },
  {
    icon: "ri-user-shared-line",
    title: "把提供者放在第一位",
    desc: "当线索的公共价值与提供者的安全发生冲突时，我们优先选择安全，必要时宁可延后甚至放弃呈现。",
  },
];

const philosophy = [
  {
    title: "信任是流程挣来的",
    desc: "我们不会只靠一句「请相信我们」。不记录 IP、最小知悉范围、呈现前与你确认，这些具体的做法才是信任的基础。",
  },
  {
    title: "慢一点，稳一点",
    desc: "未经核实的内容一旦扩散，伤害往往不可逆。我们宁愿多花几天交叉验证，也不抢那一点时效。",
  },
  {
    title: "允许你反悔",
    desc: "提供线索不是签下契约。在内容公开之前，你随时可以叫停。我们不会因为你改变主意而有任何为难。",
  },
];

const principles = [
  { title: "核实优先", desc: "未经交叉核验的内容不进入呈现流程，孤证与传闻不得单独使用。" },
  { title: "最小伤害", desc: "避免伤及与公共议题无关的第三方，涉及未成年人时从严处理。" },
  { title: "可追溯", desc: "每个关键结论都对应可查证的材料出处，而非印象或推测。" },
  { title: "公开更正", desc: "若已呈现的内容出现事实错误，我们会在同一位置公开更正，而不是悄悄删除。" },
  { title: "不涉费用", desc: "不向线索提供者收取任何费用，也不接受以撤稿为条件的任何交易。" },
  { title: "保持独立", desc: "不隶属于任何商业机构，不被单一委托方决定什么值得被核实。" },
];

const roles = [
  { icon: "ri-inbox-archive-line", title: "线索接收与登记", desc: "负责第一时间接住线索，登记关键要素，并判断是否需要加急处理。" },
  { icon: "ri-search-eye-line", title: "事实核查", desc: "负责与公开信息、旁证材料交叉比对，确认每一个关键事实的成立与否。" },
  { icon: "ri-database-2-line", title: "数据与文件分析", desc: "负责处理账目、报表、系统记录等结构化材料，寻找口径差异与异常点。" },
  { icon: "ri-shield-star-line", title: "安全与脱敏", desc: "负责评估提供者风险，执行文件与身份脱敏，并制定后续保护方案。" },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们｜暗线独家爆料线索征集中心",
    description:
      "暗线由独立核实小组运营，专注独家爆料线索征集与核验。这里说明我们的网站定位、发展理念、六条内容原则、核实小组分工与联系方式。",
    keywords: "暗线爆料,独家爆料征集,线索核实机构,独立核实小组,爆料平台介绍",
    path: "/about",
    schema: aboutSchema,
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <PageHero
          eyebrow="ABOUT US · 关于我们"
          title="一个只接收真相碎片的地方"
          description="暗线由一支独立核实小组运营。我们不做资讯、不追热点，只做一件事：把知情者手里的材料和公共信息之间那道缝，补起来。"
          crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
          image="https://readdy.ai/api/search-image?query=empty%20low%20key%20newsroom%20desk%20at%20night%20with%20a%20vintage%20typewriter%2C%20stacked%20newspapers%20and%20a%20small%20brass%20lamp%2C%20deep%20charcoal%20shadows%2C%20warm%20amber%20pool%20of%20light%2C%20crimson%20subtle%20rim%2C%20fine%20film%20grain%2C%20cinematic%20editorial%20photography&width=1920&height=900&seq=anxian-about-hero&orientation=landscape"
          imageAlt="夜间编辑部桌面上的一盏黄铜台灯与打字机，代表独立核实小组"
          meta={
            <p className="text-xs tracking-[0.16em] text-background-200/70">{siteBrand.operatorLine}</p>
          }
        />

        <section className="w-full bg-background-50 py-16 md:py-24">
          <div className="mx-auto w-full max-w-[1320px] px-5 md:px-8">
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
              <div>
                <span className="text-xs font-medium tracking-[0.22em] text-primary-600">WHO WE ARE</span>
                <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                  我们是谁
                </h2>
                <div className="mt-5 space-y-4 text-sm leading-7 text-foreground-600 md:text-[15px] md:leading-8">
                  <p>
                    暗线起步于一个很具体的问题：很多人手里握着能说明问题的材料，却找不到一个愿意认真听完、并且不把他们置于危险中的地方。
                  </p>
                  <p>
                    我们因此把站点做成一条通道，而不是一份刊物。所有栏目、所有文案、所有流程，都是为了降低你把材料交出来的心理成本与实际风险。
                  </p>
                  <p>
                    我们不承诺惊天动地的结果，只承诺两件事：每一条线索都会被读；每一个提供者都会被当作需要保护的人，而不是消息源。
                  </p>
                </div>
              </div>

              <div className="h-[300px] w-full overflow-hidden rounded-xl border border-background-200 md:h-[420px]">
                <img
                  src="https://readdy.ai/api/search-image?query=dark%20table%20top%20scene%20with%20several%20open%20notebooks%2C%20pinned%20index%20cards%20connected%20by%20red%20thread%20and%20a%20magnifier%2C%20warm%20lamp%20light%20pool%2C%20deep%20charcoal%20surroundings%2C%20fine%20film%20grain%2C%20cinematic%20investigative%20still%20life%2C%20no%20people&width=1200&height=1000&seq=anxian-about-team&orientation=landscape"
                  alt="用红线连接的索引卡片与笔记本，代表线索核实与交叉比对"
                  title="关于暗线 - 独家爆料线索的核实方法"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-16 md:py-24">
          <div className="mx-auto w-full max-w-[1320px] px-5 md:px-8">
            <h2 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              网站定位
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600 md:text-[15px]">
              在信息过载的时代，克制是一种能力。我们把自己限定在很窄的范围内，换来的是流程上的确定性。
            </p>

            <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
              {positioning.map((item, index) => (
                <Reveal key={item.title} delay={index * 70}>
                  <article className="h-full rounded-lg border border-background-200 bg-background-50 p-6 md:p-7">
                    <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className={`${item.icon} text-2xl`}></i>
                    </span>
                    <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">{item.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-foreground-600">{item.desc}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-foreground-950 py-16 md:py-24">
          <div className="mx-auto w-full max-w-[1320px] px-5 md:px-8">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-20">
              <div>
                <span className="text-xs font-medium tracking-[0.22em] text-accent-300">PHILOSOPHY</span>
                <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-background-50 md:text-4xl">
                  发展理念
                </h2>
                <p className="mt-5 text-sm leading-7 text-background-200/80 md:text-[15px]">
                  三句话概括我们做判断时的取舍顺序。它们不宏大，但在具体分岔路口上，确实能替我们做决定。
                </p>
              </div>

              <div className="space-y-6">
                {philosophy.map((item, index) => (
                  <Reveal key={item.title} delay={index * 80}>
                    <div className="flex gap-5 border-l-2 border-accent-400/50 pl-6">
                      <div>
                        <h3 className="font-heading text-lg font-semibold text-background-50">{item.title}</h3>
                        <p className="mt-2 text-sm leading-7 text-background-200/80">{item.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-16 md:py-24">
          <div className="mx-auto w-full max-w-[1320px] px-5 md:px-8">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)] lg:gap-16">
              <div className="lg:sticky lg:top-28 lg:self-start">
                <span className="text-xs font-medium tracking-[0.22em] text-primary-600">EDITORIAL PRINCIPLES</span>
                <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                  内容原则
                </h2>
                <p className="mt-4 text-sm leading-7 text-foreground-600 md:text-[15px]">
                  六条原则，公开发布，也接受监督。如果你认为我们在某件事上没有做到，欢迎写信指出。
                </p>
              </div>

              <ol className="grid grid-cols-1 gap-x-8 gap-y-7 sm:grid-cols-2">
                {principles.map((item, index) => (
                  <li key={item.title} className="border-t border-background-200 pt-5">
                    <div className="flex items-baseline gap-3">
                      <span className="font-heading text-sm font-bold text-accent-600">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                    </div>
                    <p className="mt-2.5 text-sm leading-7 text-foreground-600">{item.desc}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-16 md:py-24">
          <div className="mx-auto w-full max-w-[1320px] px-5 md:px-8">
            <h2 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              核实小组的分工
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600 md:text-[15px]">
              我们不公开成员身份，但可以说明一条线索会经过哪些角色。这是流程的一部分，也是相互制衡的一部分。
            </p>

            <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {roles.map((role, index) => (
                <Reveal key={role.title} delay={index * 60}>
                  <article className="h-full rounded-lg border border-background-200 bg-background-50 p-6">
                    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                      <i className={`${role.icon} text-xl`}></i>
                    </span>
                    <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{role.title}</h3>
                    <p className="mt-2 text-sm leading-7 text-foreground-600">{role.desc}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-16 md:py-24">
          <div className="mx-auto w-full max-w-[1320px] px-5 md:px-8">
            <h2 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              提供者怎么说
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600 md:text-[15px]">
              以下反馈均来自匿名提供者，身份信息已按约定处理，仅保留必要的背景描述。
            </p>

            <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
              {voices.map((voice, index) => (
                <Reveal key={voice.meta} delay={index * 70}>
                  <blockquote className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6">
                    <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                    <p className="mt-3 flex-1 text-sm leading-7 text-foreground-700">{voice.quote}</p>
                    <footer className="mt-5 border-t border-background-200 pt-4 text-xs text-foreground-500">
                      {voice.meta}
                    </footer>
                  </blockquote>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-foreground-950 py-16 md:py-20">
          <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-8 px-5 md:flex-row md:items-center md:justify-between md:px-8">
            <div>
              <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">联系方式</h2>
              <p className="mt-3 max-w-xl text-sm leading-7 text-background-200/80">
                线索投递、内容质疑与流程建议，都可以发到下面的邮箱。若涉及敏感材料，请先阅读投稿指南再做决定。
              </p>
              <a
                href={`mailto:${siteBrand.email}`}
                className="mt-5 inline-flex break-all font-heading text-lg font-semibold text-accent-300 hover:text-accent-400"
              >
                {siteBrand.email}
              </a>
            </div>
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
              <Link
                to="/submit"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-shield-keyhole-line text-base"></i>
                提交线索
              </Link>
              <Link
                to="/guide"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/25 px-7 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:border-accent-400 hover:text-accent-300"
              >
                阅读投稿指南
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}